import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { FiMail, FiSave, FiCheckCircle, FiServer, FiLock, FiUser, FiAtSign, FiArrowLeft } from 'react-icons/fi';

const EmailConfig = () => {
    const [formData, setFormData] = useState({
        smtp_server: '',
        smtp_port: 587,
        smtp_user: '',
        smtp_password: '',
        use_tls: true,
        sender_email: '',
        sender_name: 'Confirma AI'
    });
    const [loading, setLoading] = useState(false);
    const [testing, setTesting] = useState(false);
    const [feedback, setFeedback] = useState({ type: '', text: '' });

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            const response = await api.get('/email-config');
            if (response.data && response.data.smtp_server) {
                setFormData(prev => ({
                    ...prev,
                    ...response.data,
                    smtp_password: '' // Don't show password
                }));
            }
        } catch (err) {
            console.error("Erro ao carregar configurações", err);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        setFeedback({});
        try {
            await api.post('/email-config', formData);
            setFeedback({ type: 'success', text: 'Configurações salvas com sucesso!' });
        } catch (err) {
            setFeedback({ type: 'danger', text: err.response?.data?.msg || 'Erro ao salvar configurações.' });
        } finally {
            setLoading(false);
        }
    };

    const handleTest = async () => {
        if (!formData.smtp_server || !formData.sender_email) {
            setFeedback({ type: 'warning', text: 'Preencha os campos obrigatórios antes de testar.' });
            return;
        }
        setTesting(true);
        setFeedback({});
        try {
            await api.post('/email-config/test', formData);
            setFeedback({ type: 'success', text: 'E-mail de teste enviado com sucesso! Verifique sua caixa de entrada.' });
        } catch (err) {
            console.error("Erro no teste de email:", err);
            const msg = err.response?.data?.msg || err.message || 'Falha desconhecida no teste de conexão.';
            setFeedback({ type: 'danger', text: msg });
        } finally {
            setTesting(false);
        }
    };

    return (
        <Container className="mt-4">
            <div className="mb-4">
                <Button 
                    variant="link" 
                    as={Link} 
                    to="/training-list" 
                    className="text-decoration-none p-0 mb-2 text-muted"
                >
                    <FiArrowLeft className="me-1" /> Voltar para Lista de treinamento
                </Button>               
                
            </div>
            <div className="d-flex justify-content-between align-items-center">
                <h2 className="mb-4 fw-bold" style={{ fontFamily: 'Nunito, sans-serif', color: '#006064' }}>
                    Configuração de E-mail
                </h2>
                
            </div>
            <div className="d-flex justify-content-between align-items-center">
                <p className="text-muted">Gerencie as configurações do servidor SMTP para envio de notificações.</p>
            </div>
            
            {feedback.text && <Alert variant={feedback.type} dismissible onClose={() => setFeedback({})}>{feedback.text}</Alert>}

            <Card className="shadow-sm">
                <Card.Body className="p-4">
                    <Form onSubmit={handleSave}>
                        <h5 className="mb-4 fw-bold d-flex align-items-center gap-2">
                            <FiServer className="text-primary" /> Servidor SMTP
                        </h5>
                        <Row className="g-3 mb-4">
                            <Col md={8}>
                                <Form.Group>
                                    <Form.Label><FiServer className="me-1 text-primary"/> Host (Servidor)</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="smtp_server" 
                                        value={formData.smtp_server} 
                                        onChange={handleChange} 
                                        placeholder="smtp.suaempresa.com" 
                                        required 
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Porta</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        name="smtp_port" 
                                        value={formData.smtp_port} 
                                        onChange={handleChange} 
                                        required 
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Check 
                                    type="switch"
                                    id="tls-switch"
                                    label="Usar TLS/SSL"
                                    name="use_tls"
                                    checked={formData.use_tls}
                                    onChange={handleChange}
                                />
                            </Col>
                        </Row>

                        <h5 className="mb-4 fw-bold d-flex align-items-center gap-2 mt-4">
                            <FiLock className="text-primary" /> Autenticação
                        </h5>
                        <Row className="g-3 mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="text-muted small fw-bold">Usuário</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="smtp_user" 
                                        value={formData.smtp_user} 
                                        onChange={handleChange} 
                                        placeholder="seu@email.com"
                                        required 
                                        className="bg-light py-2"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="text-muted small fw-bold">Senha</Form.Label>
                                    <Form.Control 
                                        type="password" 
                                        name="smtp_password" 
                                        value={formData.smtp_password} 
                                        onChange={handleChange} 
                                        placeholder="Senha do app ou e-mail"
                                        className="bg-light py-2"
                                    />
                                    <Form.Text className="text-muted">
                                        Deixe em branco para manter a senha atual.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <h5 className="mb-4 fw-bold d-flex align-items-center gap-2 mt-4">
                            <FiUser className="text-primary" /> Remetente
                        </h5>
                        <Row className="g-3 mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="text-muted small fw-bold">Nome do Remetente</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="sender_name" 
                                        value={formData.sender_name} 
                                        onChange={handleChange}
                                        className="bg-light py-2" 
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="text-muted small fw-bold">E-mail do Remetente</Form.Label>
                                    <Form.Control 
                                        type="email" 
                                        name="sender_email" 
                                        value={formData.sender_email} 
                                        onChange={handleChange} 
                                        required 
                                        className="bg-light py-2"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex gap-2 justify-content-end pt-3 border-top">
                            <Button variant="outline-primary" onClick={handleTest} disabled={testing || loading} className="px-4">
                                {testing ? <Spinner size="sm" animation="border" /> : <><FiCheckCircle className="me-2"/> Testar Conexão</>}
                            </Button>
                            <Button variant="success" type="submit" disabled={loading || testing} className="px-4 fw-bold">
                                {loading ? <Spinner size="sm" animation="border" /> : <><FiSave className="me-2"/> Salvar Configurações</>}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default EmailConfig;
